// The character document. `v` is the schema version — bump when we make
// breaking changes to the serialized form, then add a migration.

export type CharacterType = 'grog' | 'companion' | 'magus';

export interface Characteristics {
  // Ars Magica's 8 characteristics, range -3..+3 (with pts 0..3 squared).
  intelligence: number;
  perception: number;
  strength: number;
  stamina: number;
  presence: number;
  communication: number;
  dexterity: number;
  quickness: number;
}

export interface ChosenVirtue {
  id: string;
  /**
   * For virtues whose dataset category is 'either', this resolves to
   * 'major' or 'minor' when the player commits the choice. Used by the
   * budget layer to compute concrete point cost.
   */
  chosenCategory?: 'major' | 'minor';
  /** Free-text hook slot for virtues that instantiate a sub-choice (focus, area, ability name). */
  subject?: string;
  notes?: string;
}

export interface ChosenFlaw {
  id: string;
  chosenCategory?: 'major' | 'minor';
  subject?: string;
  notes?: string;
}

export interface AbilityScore {
  id: string;
  xp: number; // raw XP, score derives via triangular formula
  specialization?: string;
}

export interface ArtScore {
  id: string; // e.g. "creo", "ignem"
  xp: number;
}

export interface ChosenSpell {
  id?: string; // optional — may be a custom spell
  name: string;
  technique: string;
  form: string;
  level: number | null;
  /** 'general' for spells whose level scales at cast-time. */
  levelBand?: 'general' | null;
  range?: string;
  duration?: string;
  target?: string;
  mastery?: number;
  notes?: string;
}

export interface PersonalityTrait {
  name: string;
  score: number; // -3..+3 typical
}

export interface Reputation {
  name: string;
  realm: string; // e.g. "Hermetic", "Church", "Local"
  score: number;
}

export interface GrogFields {}
export interface CompanionFields {
  mythic?: boolean;
  mythicKind?: string;
}
export interface MagusFields {
  house?: string;
  sigil?: string;
  votingSigil?: string;
  magicalFocus?: string;
  arts: ArtScore[];
  spells: ChosenSpell[];
  twilightScars?: string[];
  longevityBonus?: number;
  familiar?: {
    name: string;
    species?: string;
    might?: number;
    size?: number;
    goldenCord?: 0 | 1 | 2 | 3 | 4 | 5;
    silverCord?: 0 | 1 | 2 | 3 | 4 | 5;
    bronzeCord?: 0 | 1 | 2 | 3 | 4 | 5;
    boundLabTotal?: number;
    notes?: string;
  };
  lab?: {
    size?: number;
    refinement?: number;
    generalQuality?: number;
    upkeep?: number;
    safety?: number;
    warping?: number;
    health?: number;
    aesthetics?: number;
    specializations?: string[];
    notes?: string;
  };
  sanctum?: string;
}

export interface StoryHook {
  kind: 'dependent' | 'enemy' | 'favor' | 'rival' | 'mentor' | 'other';
  name: string;
  notes?: string;
  flawId?: string;
}

export interface Wounds {
  light: number;
  medium: number;
  heavy: number;
  incapacitated: number;
  dead?: boolean;
}

export interface CharacterV1 {
  v: 1;
  type: CharacterType;
  name: string;
  age?: number;
  apparentAge?: number;
  gender?: string;
  birthplace?: string;
  nationality?: string;
  appearance?: string;
  nativeLanguage?: string;
  size?: number;

  characteristics: Characteristics;
  virtues: ChosenVirtue[];
  flaws: ChosenFlaw[];
  abilities: AbilityScore[];
  personality: PersonalityTrait[];
  reputations: Reputation[];
  confidenceScore?: number;
  confidence?: number;

  /** Warping applies to every character type, not just magi. */
  warpingScore?: number;
  warpingPoints?: number;
  /** Decrepitude applies to every character type. */
  decrepitudeScore?: number;
  decrepitudePoints?: number;
  /** Per-characteristic aging counters; a Characteristic drops when its points exceed |score|. */
  agingPoints?: Partial<Record<keyof Characteristics, number>>;

  fatigueLevel?: 0 | 1 | 2 | 3 | 4 | 5;
  wounds?: Wounds;

  storyHooks?: StoryHook[];
  covenant?: { name?: string; role?: string; joined?: number };

  // Type-specific payload.
  grog?: GrogFields;
  companion?: CompanionFields;
  magus?: MagusFields;

  notes?: string;
  updatedAt: string;
}

export type Character = CharacterV1;
export const CURRENT_SCHEMA_VERSION = 1 as const;

export function blankCharacter(type: CharacterType): Character {
  const now = new Date().toISOString();
  const c: Character = {
    v: 1,
    type,
    name: '',
    size: 0, // average human
    warpingScore: 0,
    warpingPoints: 0,
    decrepitudeScore: 0,
    decrepitudePoints: 0,
    fatigueLevel: 0,
    wounds: { light: 0, medium: 0, heavy: 0, incapacitated: 0 },
    characteristics: {
      intelligence: 0,
      perception: 0,
      strength: 0,
      stamina: 0,
      presence: 0,
      communication: 0,
      dexterity: 0,
      quickness: 0,
    },
    virtues: [],
    flaws: [],
    abilities: [],
    personality: [],
    reputations: [],
    updatedAt: now,
  };
  if (type === 'grog') {
    c.grog = {};
    // Grogs should have the Loyal personality trait by default.
    c.personality = [{ name: 'Loyal', score: 0 }];
  }
  if (type === 'companion') {
    c.companion = {};
    c.confidenceScore = 1;
    c.confidence = 3;
  }
  if (type === 'magus') {
    c.magus = { arts: [], spells: [] };
    c.virtues = [{ id: 'the-gift' }, { id: 'hermetic-magus' }];
    c.abilities = [
      { id: 'parma-magica', xp: 5 },
      { id: 'magic-theory', xp: 5 },
      { id: 'dead-language', xp: 5, specialization: 'Latin' },
    ];
    c.nativeLanguage = 'Latin';
    c.confidenceScore = 1;
    c.confidence = 3;
  }
  return c;
}
